import React, { useMemo, useState, useEffect } from "react";

import {
  Box,
  Typography,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Tooltip,
} from "@mui/material/";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { SingleLocationAvailabilityReportResponses } from "../service/availabilityReportInterfaces";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import dayjs from "dayjs";
import { formatPositiveNumber, formatNegativeNumber } from "../../../utils";
import { useTranslation } from "react-i18next";
import EnumChangeName from "../../common/EnumChangeName";
import { useRecoilValue } from "recoil";
import { reportOldTimezoneState } from "./AvailabilityReportOld";

const SingleLocationTableOld: React.FC<
  SingleLocationAvailabilityReportResponses
> = ({ location, rowsPerAcriss, configurationThresholds, fullDays, index, totalRowsByLocation, totalCurrentAvailability, totalCurrentUtilization }) => {
  const { t } = useTranslation(["operation"]);
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const defaultColDef: ColDef = {
    suppressMovable: true,
  };
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const [rowData, setRowData] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<boolean>(index === 0 ? true : false);

  const { locationType } = EnumChangeName();

  const isNullOrUndefined = (value: any) => {
    return typeof value === "undefined" || value === null;
  };

  const availabilityLow =
    configurationThresholds?.filter(
      (item) => item.thresholdName === "AVAILABILITY_MEDIUM"
    )[0].minValueIncluded || 0;

  const availabilityBelow0 =
    configurationThresholds?.filter(
      (item) => item.thresholdName === "AVAILABILITY_BELOW0"
    )[0].maxValueExcluded || 0;

  const availabilityHigh =
    configurationThresholds?.filter(
      (item) => item.thresholdName === "AVAILABILITY_MEDIUM"
    )[0].maxValueExcluded || 0;

  const utilizationLow =
    configurationThresholds?.filter(
      (item) => item.thresholdName === "UTILIZATION_LOW"
    )[0].maxValueExcluded || 0;

  const utilizationHigh =
    configurationThresholds?.filter(
      (item) => item.thresholdName === "UTILIZATION_GOOD"
    )[0].maxValueExcluded || 0;

  const cellClassRules = {
    boldLabels: (params: { colDef: ColDef; data: any }) =>
      (params.colDef.field === "acrissLabel" && params.data.isAcriss) ||
      params.data.acrissLabel === "UTILIZATION",
    red: (params: { data: any; value: any }) =>
      typeof params.value !== "string" &&
      params.data.acrissLabel === "UTILIZATION" &&
      params.value >= utilizationHigh * 100,
    yellow: (params: { data: any; value: any }) =>
      typeof params.value !== "string" &&
      params.data.acrissLabel === "UTILIZATION" &&
      params.value >= utilizationLow * 100 &&
      params.value < utilizationHigh * 100,
    green: (params: { data: any; value: any }) =>
      typeof params.value !== "string" &&
      params.data.acrissLabel === "UTILIZATION" &&
      params.value < utilizationLow * 100,
    fontWhite: (params: { data: any; value: any }) =>
      typeof params.value !== "string" &&
      params.data.acrissLabel === "UTILIZATION",
    grey: (params: { data: any; value: any }) =>
      (typeof params.value === "string" &&
        params.data.acrissLabel === "UTILIZATION") ||
      (isNaN(params.value) && params.data.acrissLabel === "UTILIZATION"),

    borderBottom: (params: { data: any }) =>
      params.data.acrissLabel === "UTILIZATION",
    borderRight: (params: { colDef: ColDef }) =>
      params.colDef.headerName === "",
  };

  const circleColor = (value: number) => {
    if (value < availabilityBelow0) {
      return "grey";
    }

    if (value < availabilityLow) {
      return "red";
    }

    if (value >= availabilityHigh) {
      return "green";
    }

    return "yellow";
  };

  const getTotalRows = () => {
    if (!totalRowsByLocation) {
      return [];
    }

    const totalRows: any[] = [];
    const dateValues: any = {};
    const utilizationValues: any = {};
    totalRowsByLocation.forEach((el) => {
      const currentDate = fullDays
      ? `${dayjs(el.previewDate).format("DD")}-${dayjs(
          el.previewDate
        ).format("MM")}-${dayjs(el.previewDate).format("YY")}`
      : `${dayjs(el.previewDate).format("DD")}-${dayjs(
          el.previewDate
        ).format("MM")}-${dayjs(el.previewDate).format("YY")}-${dayjs(
          el.previewDate
        ).format("A")}`;

      // date values
      dateValues[currentDate] = el.availability;
      dateValues[`${currentDate}-startReservationNeed`] =
        el.startReservationNeed;
      dateValues[`${currentDate}-startUnavailabilityNeed`] =
        el.startUnavailabilityNeed;
      dateValues[`${currentDate}-sellOrderNeed`] = el.sellOrderNeed;
      dateValues[`${currentDate}-endReservationSupply`] =
        el.endReservationSupply;
      dateValues[`${currentDate}-endUnavailabilitySupply`] =
        el.endUnavailabilitySupply;
      dateValues[`${currentDate}-purchaseOrderSupply`] =
        el.purchaseOrderSupply;

      // utilization values
      utilizationValues[currentDate] = +(el.utilization * 100).toFixed(0);
      utilizationValues[`${currentDate}-assignation`] = el.assignation;
      utilizationValues[`${currentDate}-stock`] = el.stock;
    });
      
    totalRows.push({
      acrissLabel: t("availabilityReport.total"),
      acrissTooltip: t("availabilityReport.total"),
      acrissCategory: "TOTAL",
      current: totalCurrentAvailability,
      isAcriss: false,
      isVisible: true,
      childrenHidden: false,
      ...dateValues,
    });

    totalRows.push({
      acrissLabel: "UTILIZATION",
      current: +(totalCurrentUtilization * 100).toFixed(0),
      isAcriss: false,
      isVisible: true,
      childrenHidden: false,
      ...utilizationValues,
    });
    return totalRows;
  }

  const handleRowAcriss = () => {
    let allRows: any[] = [];
    const dateValues: any = {};
    const utilizationValues: any = {};
    rowsPerAcriss.map((rowPerAcriss) => {
      // acriss
      rowPerAcriss.aggregatedRows.forEach((el) => {
        const currentDate = fullDays
          ? `${dayjs(el.previewDate).format("DD")}-${dayjs(
              el.previewDate
            ).format("MM")}-${dayjs(el.previewDate).format("YY")}`
          : `${dayjs(el.previewDate).format("DD")}-${dayjs(
              el.previewDate
            ).format("MM")}-${dayjs(el.previewDate).format("YY")}-${dayjs(
              el.previewDate
            ).format("A")}`;
        dateValues[currentDate] = el.availability;
        dateValues[`${currentDate}-startReservationNeed`] =
          el.startReservationNeed;
        dateValues[`${currentDate}-startUnavailabilityNeed`] =
          el.startUnavailabilityNeed;
        dateValues[`${currentDate}-sellOrderNeed`] = el.sellOrderNeed;
        dateValues[`${currentDate}-endReservationSupply`] =
          el.endReservationSupply;
        dateValues[`${currentDate}-endUnavailabilitySupply`] =
          el.endUnavailabilitySupply;
        dateValues[`${currentDate}-purchaseOrderSupply`] =
          el.purchaseOrderSupply;
      });

      allRows.push({
        acrissLabel: rowPerAcriss.acriss,
        acrissTooltip: rowPerAcriss.acrissLabel,
        acrissCategory: rowPerAcriss.acriss,
        current: rowPerAcriss.currentAvailability,
        isAcriss: true,
        isVisible: true,
        childrenHidden: false,
        ...dateValues,
      });

      // singleSipps
      rowPerAcriss.singleSipps
        .filter((item) => !!item.sipp)
        .forEach((singleSipp) => {
          singleSipp.sippRows.forEach((el) => {
            const currentDate = fullDays
              ? `${dayjs(el.previewDate).format("DD")}-${dayjs(
                  el.previewDate
                ).format("MM")}-${dayjs(el.previewDate).format("YY")}`
              : `${dayjs(el.previewDate).format("DD")}-${dayjs(
                  el.previewDate
                ).format("MM")}-${dayjs(el.previewDate).format("YY")}-${dayjs(
                  el.previewDate
                ).format("A")}`;
            dateValues[currentDate] = el.availability;
            dateValues[`${currentDate}-startReservationNeed`] =
              el.startReservationNeed;
            dateValues[`${currentDate}-startUnavailabilityNeed`] =
              el.startUnavailabilityNeed;
            dateValues[`${currentDate}-sellOrderNeed`] = el.sellOrderNeed;
            dateValues[`${currentDate}-endReservationSupply`] =
              el.endReservationSupply;
            dateValues[`${currentDate}-endUnavailabilitySupply`] =
              el.endUnavailabilitySupply;
            dateValues[`${currentDate}-purchaseOrderSupply`] =
              el.purchaseOrderSupply;
          });
          allRows.push({
            acrissLabel: singleSipp.sipp,
            acrissTooltip: singleSipp.sippLabel,
            acrissCategory: rowPerAcriss.acriss,
            current: singleSipp.currentAvailability,
            isAcriss: false,
            isVisible: true,
            childrenHidden: false,
            ...dateValues,
          });
        });

      //utilization
      rowPerAcriss.aggregatedRows.forEach((el) => {
        const currentDate = fullDays
          ? `${dayjs(el.previewDate).format("DD")}-${dayjs(
              el.previewDate
            ).format("MM")}-${dayjs(el.previewDate).format("YY")}`
          : `${dayjs(el.previewDate).format("DD")}-${dayjs(
              el.previewDate
            ).format("MM")}-${dayjs(el.previewDate).format("YY")}-${dayjs(
              el.previewDate
            ).format("A")}`;
        utilizationValues[currentDate] = +(el.utilization * 100).toFixed(0);
        utilizationValues[`${currentDate}-assignation`] = el.assignation;
        utilizationValues[`${currentDate}-stock`] = el.stock;
      });

      allRows.push({
        acrissLabel: "UTILIZATION",
        acrissCategory: rowPerAcriss.acriss,
        current: !isNullOrUndefined(rowPerAcriss.currentUtilization)
          ? +((rowPerAcriss.currentUtilization as number) * 100).toFixed(0)
          : NaN,
        isAcriss: false,
        isVisible: true,
        childrenHidden: false,
        ...utilizationValues,
      });

      // totals Row
      let totalRows = getTotalRows();
      
      setRowData([
        ...rowData,
        ...totalRows, 
        ...allRows
      ]);
    });
  };

  useEffect(() => {
    handleRowAcriss();
  }, []);

  const renderAvailabilityToolptip = (
    startReservationNeed: number,
    startUnavailabilityNeed: number,
    sellOrderNeed: number,
    endReservationSupply: number,
    endUnavailabilitySupply: number,
    purchaseOrderSupply: number,
    value: number
  ) => {
    return (
      <div>
        {t("availabilityReport.tooltipTitles.reservationsStarted")}
        <span style={{ float: "right", marginLeft: "10px" }}>
          {startReservationNeed ? "-" : ""}
          {startReservationNeed}
        </span>
        <br />
        {t("availabilityReport.tooltipTitles.unavailabiltiesStarted")}
        <span style={{ float: "right", marginLeft: "10px" }}>
          {startUnavailabilityNeed ? "-" : ""}
          {startUnavailabilityNeed}
        </span>
        <br />
        {t("availabilityReport.tooltipTitles.vehiclesSold")}
        <span style={{ float: "right", marginLeft: "10px" }}>
          {sellOrderNeed ? "-" : ""}
          {sellOrderNeed}
        </span>
        <br />
        {t("availabilityReport.tooltipTitles.reservationsEnded")}
        <span style={{ float: "right", marginLeft: "10px" }}>
          {endReservationSupply}
        </span>
        <br />
        {t("availabilityReport.tooltipTitles.unavailabilitiesEnded")}
        <span style={{ float: "right", marginLeft: "10px" }}>
          {endUnavailabilitySupply}
        </span>
        <br />
        {t("availabilityReport.tooltipTitles.vehiclePurchased")}
        <span style={{ float: "right", marginLeft: "10px" }}>
          {purchaseOrderSupply}
        </span>

        <br />

        <div
          style={{
            backgroundColor: "rgb(255,255,255, 0.5)",
            marginTop: "10px",
            marginLeft: "-16px",
            marginRight: "-16px",
            paddingLeft: "16px",
            paddingRight: "16px",
          }}
        >
          {t("availabilityReport.tooltipTitles.total")}
          <span style={{ float: "right", marginLeft: "10px" }}>{value}</span>
        </div>
      </div>
    );
  };

  const renderUtilizationTooltip = (
    assignation: number,
    stock: number,
    value: number,
    arrowValues: any
  ) => {
    return (
      <div>
        {t("availabilityReport.tooltipTitles.assignation")}
        <span style={{ float: "right", marginLeft: "10px" }}>
          {assignation}
        </span>
        <br />
        {t("availabilityReport.tooltipTitles.stock")}
        <span style={{ float: "right", marginLeft: "10px" }}>{stock}</span>
        <br />

        {!isNaN(value) && (
          <div
            style={{
              backgroundColor: "rgb(255,255,255, 0.5)",
              marginTop: "10px",
              marginLeft: "-16px",
              marginRight: "-16px",
              paddingLeft: "16px",
              paddingRight: "16px",
            }}
          >
            {t("availabilityReport.tooltipTitles.ratio")}
            <span
              style={{ float: "right", marginLeft: "10px" }}
            >{`${value}%`}</span>
          </div>
        )}

        <div>
          {!isNaN(value) ? (
            arrowValues?.values?.down || arrowValues?.values?.up ? (
              <div>
                <br />
                {arrowValues?.values?.down && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    {arrowValues?.values?.downIcon}

                    <div style={{ width: "100%", marginLeft: "5px" }}>
                      {(arrowValues?.values?.down || []).map((item: any) => {
                        return (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{item.title}:</span>
                            <span style={{ marginLeft: 2 }}>{item.value}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {arrowValues?.values?.up && (
                  <div
                    style={{
                      display: "flex",
                      marginTop: 20,
                    }}
                  >
                    {arrowValues?.values?.upIcon}

                    <div style={{ width: "100%", marginLeft: "5px" }}>
                      {(arrowValues?.values?.up || []).map((item: any) => {
                        return (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{item.title}:</span>
                            <span style={{ marginLeft: 2 }}>{item.value}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              ``
            )
          ) : (
            <>
              <br />
              {t("availabilityReport.tooltipTitles.notAvailable")}
            </>
          )}
        </div>
      </div>
    );
  };

  const timezone = useRecoilValue(reportOldTimezoneState);
  
  const timezoneOffset = useMemo(() => {

    const offsetMinutes = dayjs().tz(timezone).utcOffset(); // minutes
    return offsetMinutes / 60; // hours
  }, []);

  const renderAcrissLabel = (params: ICellRendererParams) => {
    return params.value !== "UTILIZATION" ? (
      <Tooltip arrow={true} title={params.data.acrissTooltip}>
        <span className="toggleIcons">
          {params.data.isAcriss && params.data.childrenHidden && (
            <AddIcon
              onClick={() => handleToggleRows(params)}
              sx={{ cursor: "pointer" }}
            />
          )}
          {params.data.isAcriss && !params.data.childrenHidden && (
            <RemoveIcon
              onClick={() => handleToggleRows(params)}
              sx={{ cursor: "pointer" }}
            />
          )}
          {params.value}
        </span>
      </Tooltip>
    ) : (
      <span className="toggleIcons">
        {params.data.isAcriss && params.data.childrenHidden && (
          <AddIcon
            onClick={() => handleToggleRows(params)}
            sx={{ cursor: "pointer" }}
          />
        )}
        {params.data.isAcriss && !params.data.childrenHidden && (
          <RemoveIcon
            onClick={() => handleToggleRows(params)}
            sx={{ cursor: "pointer" }}
          />
        )}

        {t("availabilityReport.utilization")}
      </span>
    );
  };

  useEffect(() => {
    setColumnDefs([
      {
        field: "acrissLabel",
        headerName: "",
        width: 150,
        pinned: "left",
        cellClassRules: cellClassRules,
        headerClass: "borderRight",
        cellRenderer: (params: ICellRendererParams) =>
          renderAcrissLabel(params),
      },
      {
        field: "current",
        headerName: t("availabilityReport.current"),
        width: 100,
        cellClassRules: cellClassRules,
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <span style={{ width: "100%" }}>
              {!isNaN(params.value)
                ? params.value
                : t("availabilityReport.tooltipTitles.notAvailableAchronym")}
              {!isNaN(params.value) &&
                params.data.acrissLabel === "UTILIZATION" &&
                "%"}
              {params.data.acrissLabel !== "UTILIZATION" && (
                <span className={`circle ${circleColor(params.value)}`}></span>
              )}
            </span>
          );
        },
      },
      ...rowsPerAcriss[0].aggregatedRows?.map((item: any) => {
        const arrowDirection = (isUtilization: boolean) => {
          const {
            startingElsewhereReservationSupply,
            endingElsewhereReservationNeed,
            startingElsewhereUnavailabilitySupply,
            endingElsewhereUnavailabilityNeed,
          } = item;

          if (
            (startingElsewhereReservationSupply > 0 ||
              startingElsewhereUnavailabilitySupply > 0) &&
            (endingElsewhereReservationNeed > 0 ||
              endingElsewhereUnavailabilityNeed > 0)
          ) {
            return {
              values: {
                down: [
                  {
                    title: t(
                      "availabilityReport.tooltipTitles.startingElsewhereReservationSupply"
                    ),
                    value: startingElsewhereReservationSupply,
                  },

                  {
                    title: t(
                      "availabilityReport.tooltipTitles.startingElsewhereUnavailabilitySupply"
                    ),
                    value: startingElsewhereUnavailabilitySupply,
                  },
                ],

                downIcon: (
                  <ArrowDownwardIcon
                    fontSize={isUtilization ? "medium" : "small"}
                  />
                ),

                up: [
                  {
                    title: t(
                      "availabilityReport.tooltipTitles.endingElsewhereReservationNeed"
                    ),
                    value: endingElsewhereReservationNeed,
                  },

                  {
                    title: t(
                      "availabilityReport.tooltipTitles.endingElsewhereUnavailabilityNeed"
                    ),
                    value: endingElsewhereUnavailabilityNeed,
                  },
                ],

                upIcon: (
                  <ArrowUpwardIcon
                    fontSize={isUtilization ? "medium" : "small"}
                  />
                ),
              },

              icon: (
                <div style={{ display: "flex" }}>
                  <ArrowDownwardIcon
                    fontSize={isUtilization ? "medium" : "small"}
                  />
                  <ArrowUpwardIcon
                    fontSize={isUtilization ? "medium" : "small"}
                  />
                </div>
              ),
            };
          }

          if (
            startingElsewhereReservationSupply > 0 ||
            startingElsewhereUnavailabilitySupply > 0
          ) {
            return {
              values: {
                down: [
                  {
                    title: t(
                      "availabilityReport.tooltipTitles.startingElsewhereReservationSupply"
                    ),
                    value: startingElsewhereReservationSupply,
                  },

                  {
                    title: t(
                      "availabilityReport.tooltipTitles.startingElsewhereUnavailabilitySupply"
                    ),
                    value: startingElsewhereUnavailabilitySupply,
                  },
                ],
                downIcon: (
                  <ArrowDownwardIcon
                    fontSize={isUtilization ? "medium" : "small"}
                  />
                ),
              },
              icon: (
                <ArrowDownwardIcon
                  fontSize={isUtilization ? "medium" : "small"}
                />
              ),
            };
          }

          if (
            endingElsewhereReservationNeed > 0 ||
            endingElsewhereUnavailabilityNeed > 0
          ) {
            return {
              values: {
                down: [
                  {
                    title: t(
                      "availabilityReport.tooltipTitles.endingElsewhereReservationNeed"
                    ),
                    value: endingElsewhereReservationNeed,
                  },

                  {
                    title: t(
                      "availabilityReport.tooltipTitles.endingElsewhereUnavailabilityNeed"
                    ),
                    value: endingElsewhereUnavailabilityNeed,
                  },
                ],
                downIcon: (
                  <ArrowUpwardIcon
                    fontSize={isUtilization ? "medium" : "small"}
                  />
                ),
              },
              icon: (
                <ArrowUpwardIcon
                  fontSize={isUtilization ? "medium" : "small"}
                />
              ),
            };
          }

          return null;
        };

        

        const date = dayjs(item.previewDate);
        const isAM = date.format("A") === "AM";

        const fieldBase = date.format("DD-MM-YY");
        const headerDate = date.format("DD/MM/YYYY");

        const startOfToday = dayjs().startOf("day").add(timezoneOffset, "hour");

        const formatRange = (start: dayjs.Dayjs, end: dayjs.Dayjs) =>
          `${start.format("HH:mm")} - ${end.format("HH:mm")}`;

        let headerTooltip: string;

        if (fullDays) {
          const end = startOfToday.add(24, "hour").add(-1, "minute");
          headerTooltip = formatRange(startOfToday, end);
        } else {
          if (isAM) {
            const end = startOfToday.add(12, "hour").add(-1, "minute");
            headerTooltip = formatRange(startOfToday, end);
          } else {
            const start = startOfToday.add(12, "hour");
            const end = startOfToday.add(-1, "minute");
            headerTooltip = formatRange(start, end);
          }
        }

        return {
          field: fullDays ? fieldBase : `${fieldBase}-${date.format("A")}`,
          headerName: fullDays
            ? headerDate
            : `${headerDate} ${isAM ? "🌑" : "🔅"}`,
          headerTooltip,
          width: 140,
          cellClassRules: cellClassRules,
          cellRenderer: (params: ICellRendererParams) => {
            return (
              <Tooltip
                arrow={true}
                title={
                  params.data.acrissLabel !== "UTILIZATION"
                    ? renderAvailabilityToolptip(
                        params.data[
                          `${params.colDef?.field}-startReservationNeed`
                        ],
                        params.data[
                          `${params.colDef?.field}-startUnavailabilityNeed`
                        ],
                        params.data[`${params.colDef?.field}-sellOrderNeed`],
                        params.data[
                          `${params.colDef?.field}-endReservationSupply`
                        ],
                        params.data[
                          `${params.colDef?.field}-endUnavailabilitySupply`
                        ],
                        params.data[
                          `${params.colDef?.field}-purchaseOrderSupply`
                        ],
                        params.value
                      )
                    : renderUtilizationTooltip(
                        params.data[`${params.colDef?.field}-assignation`],
                        params.data[`${params.colDef?.field}-stock`],
                        params.value,
                        arrowDirection(
                          params.data.acrissLabel === "UTILIZATION"
                        )
                      )
                }
              >
                <span
                  style={{
                    width: "100%",
                    ...(params.data.acrissLabel === "UTILIZATION" && {
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }),
                  }}
                >
                  {isNaN(params.value)
                    ? t("availabilityReport.tooltipTitles.notAvailableAchronym")
                    : params.value >= 0
                    ? formatPositiveNumber(params.value)
                    : `-${formatNegativeNumber(params.value)}`}
                  {params.data.acrissLabel === "UTILIZATION" &&
                    !isNaN(params.value) &&
                    "%"}

                  {!isNaN(params.value) &&
                    params.data.acrissLabel === "UTILIZATION" &&
                    arrowDirection(params.data.acrissLabel === "UTILIZATION")
                      ?.icon}

                  {params.data.acrissLabel !== "UTILIZATION" && (
                    <span className={`circle ${circleColor(params.value)}`} />
                  )}
                </span>
              </Tooltip>
            );
          },
        };
      }),
    ]);
  }, [rowData]);

  const handleToggleRows = (params: ICellRendererParams) => {
    setRowData(
      rowData.map((row) => {
        if (
          row.acrissCategory === params.data.acrissCategory &&
          !row.isAcriss
        ) {
          row.isVisible = !row.isVisible;
        }
        if (row.acrissCategory === params.data.acrissCategory && row.isAcriss) {
          row.childrenHidden = !row.childrenHidden;
        }
        return row;
      })
    );
  };

  const onAcrissRowClick = (
    event: React.ChangeEvent<HTMLInputElement>,
    acrissLabel: string
  ) => {
    setRowData(
      rowData.map((row) => {
        if (row.acrissCategory === acrissLabel) {
          row.isVisible = event.target.checked;
        }
        return row;
      })
    );
  };

  return (
    <Box className="tablesContainer singleLocation">
      <Accordion expanded={expanded}>
        <AccordionSummary
          expandIcon={
            expanded ? (
              <RemoveIcon
                onClick={() => setExpanded(false)}
                className="expandIcon"
              />
            ) : (
              <AddIcon
                onClick={() => setExpanded(true)}
                className="expandIcon"
              />
            )
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography className="locationName">
            {location?.locationName
              ? `${location?.locationName} (${locationType(location?.locationType)})`
              : `Missing name for location id: ${location?.locationId}`}
          </Typography>
          <div>
            {rowsPerAcriss.map((acrissRow) => (
              <FormControlLabel
                key={acrissRow.acriss}
                control={
                  <Checkbox
                    defaultChecked
                    sx={{
                      color: "rgb(200, 1, 125)",
                      "&.Mui-checked": {
                        color: "rgb(200, 1, 125)",
                      },
                    }}
                    onChange={(e) => onAcrissRowClick(e, acrissRow.acriss)}
                  />
                }
                className="locationCheckbox"
                label={acrissRow.acriss}
              />
            ))}
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <Box style={gridStyle} className="ag-theme-alpine">
              <AgGridReact
                domLayout="autoHeight"
                rowHeight={42}
                rowData={rowData?.filter((row) => row.isVisible)}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                suppressRowHoverHighlight={true}
                tooltipShowDelay={0}
              />
            </Box>
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default SingleLocationTableOld;
